// 起動時の白画面(エスカ#1303: pato for cast を開くと真っ白)を後から調査できるようにするための起動トレース。
//
// 背景: 白画面の最中に JS がどこまで進んだかがどこにも残らず、
// サーバのアクセスログだけでは「起動が途中で止まった」のか「裏から復帰しただけ」なのかも区別できなかった。
// そこで起動の節目(マイルストーン)を時刻付きで localStorage に書き残し、
// - 起動中に一定時間たっても最初の画面が表示されない → その場で記録(startup_stalled)
// - 前回の起動が最初の画面の表示まで到達しないまま終わっていた → 次回起動時に記録(startup_incomplete_previous)
// とする。後者は白画面でアプリを即終了された場合や、メインスレッドが固まってタイマーすら動かない場合の取りこぼしを拾う。
// ここには localStorage の読み書きと DOM の観察だけを置き、送信は StartupTraceService が行う。

/** 起動トレースの保存先。1起動ぶんだけ保持する */
export const STARTUP_TRACE_KEY = "startup-trace";
/**
 * index.html の先頭スクリプトが書く「HTML 読み込み時刻」。Angular が起動したら消す。
 * index.html 側はこのファイルを import できないため、キー名を変えるときは両方を直すこと。
 */
export const STARTUP_HTML_BOOT_KEY = "startup-html-boot";
/** 前回の HTML 読み込みが Angular の起動まで届かなかったときに index.html が退避する値 */
export const STARTUP_HTML_ORPHAN_KEY = "startup-html-boot-orphan";
/** index.html が記録した script/link の読み込み失敗(前回起動ぶん) */
export const STARTUP_HTML_RESOURCE_ERRORS_KEY = "startup-html-resource-errors";

// 1起動で記録するマイルストーンの上限(localStorage と送信サイズを抑える)
const MAX_MARKS = 40;
const MAX_MARK_NAME_LENGTH = 60;

/** [マイルストーン名, Angular 起動からの経過ms] */
export type StartupMark = [string, number];

export type StartupTrace = {
  /** Angular 起動時刻(epoch ms) */
  startedAt: number;
  /** index.html の先頭スクリプトが動いた時刻(epoch ms)。取れなければ null */
  htmlBootAt: number | null;
  /** WebView がページの読み込みを始めた時刻(performance.timeOrigin, epoch ms)。取れなければ null */
  navStartAt: number | null;
  /**
   * index.html を取得した通信方式(h3 / h2 / http/1.1)。取れなければ null。
   * 「端末と pato.today の接続が数十秒止まる」型の白画面が HTTP/3 に偏るかを見るため
   */
  docProtocol: string | null;
  marks: StartupMark[];
  /** 最初の画面の表示完了(ルート直下ページの ionViewDidEnter)に到達したか */
  ready: boolean;
  /** この起動について送信済みか(前回分の二重送信防止) */
  reported: boolean;
  /** 画面が表示状態だった累計ms(バックグラウンド中の時間は白画面と無関係なので除く) */
  visibleMs: number;
  webBuild: string;
  /** 認証できたユーザーID。起動が認証前に止まった場合は null */
  userId: number | null;
  /**
   * 最後に JS が動いていた時点(Angular 起動からの経過ms)。最後のマイルストーンより十分後なら
   * 「JS は動いていたが何かを待ち続けていた」、ほぼ同じなら「JS ごと固まった・終了された」と読める
   */
  aliveMs: number;
};

export const createStartupTrace = (
  now: number,
  htmlBootAt: number | null,
  webBuild: string,
  navStartAt: number | null = null,
  docProtocol: string | null = null
): StartupTrace => ({
  startedAt: now,
  htmlBootAt,
  navStartAt,
  docProtocol,
  marks: [],
  ready: false,
  reported: false,
  visibleMs: 0,
  webBuild,
  userId: null,
  aliveMs: 0,
});

/** マイルストーンを追加する。上限を超えたら捨てる(起動の後半より前半の情報の方が大事なため) */
export const addStartupMark = (
  trace: StartupTrace,
  name: string,
  now: number
): void => {
  if (trace.marks.length >= MAX_MARKS) {
    return;
  }
  trace.marks.push([
    name.slice(0, MAX_MARK_NAME_LENGTH),
    Math.max(0, now - trace.startedAt),
  ]);
};

/**
 * 一度も画面に表示されなかった起動か。iOS のプリウォーム等で裏で起動され、
 * 表示されないまま終了された起動はユーザーが白画面を見ていないため、前回分の報告から除く
 */
export const wasNeverVisible = (trace: StartupTrace): boolean =>
  trace.visibleMs === 0 &&
  !trace.marks.some(
    ([name]) => name === "app_start:visible" || name === "vis:visible"
  );

/** 送信用にマイルストーンを短い文字列にする(例: "platform_ready@120 maint_ok@480") */
export const formatStartupMarks = (marks: StartupMark[]): string =>
  marks.map(([name, ms]) => `${name}@${ms}`).join(" ");

export const lastStartupMarkName = (trace: StartupTrace): string =>
  trace.marks.length > 0 ? trace.marks[trace.marks.length - 1][0] : "none";

export const loadStartupTrace = (storage: Storage): StartupTrace | null => {
  try {
    const raw = storage.getItem(STARTUP_TRACE_KEY);
    if (!raw) {
      return null;
    }
    const trace = JSON.parse(raw) as StartupTrace;
    return trace && Array.isArray(trace.marks) ? trace : null;
  } catch {
    return null;
  }
};

export const saveStartupTrace = (
  storage: Storage,
  trace: StartupTrace
): void => {
  try {
    storage.setItem(STARTUP_TRACE_KEY, JSON.stringify(trace));
  } catch {
    // 容量超過などで書けなくても起動は妨げない
  }
};

/** epoch ms の文字列を数値にする。不正値は null */
export const parseEpoch = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** ionViewDidEnter の発火元が、アプリ直下の ion-router-outlet に載ったページ(=最初の画面)か */
export const isRootPage = (el: Element | null): boolean =>
  !!el?.parentElement?.matches("ion-app > ion-router-outlet");

/**
 * 起動の各区間の所要時間(ms)。白画面が「HTML が届くまで」「JS の読み込み〜Angular 起動まで」
 * 「Angular 起動後」のどこで長引いたかを切り分ける
 */
export const buildStartupPhases = (
  trace: StartupTrace
): { html_wait_ms?: number; html_to_app_ms?: number } => ({
  html_wait_ms:
    trace.navStartAt != null && trace.htmlBootAt != null
      ? Math.round(trace.htmlBootAt - trace.navStartAt)
      : undefined,
  html_to_app_ms:
    trace.htmlBootAt != null ? trace.startedAt - trace.htmlBootAt : undefined,
});

export type StartupNetworkTiming = {
  /** ページ(index.html)の取得の内訳(読み込み開始からの経過ms)と通信方式 */
  doc: { [key: string]: number | string } | null;
  /** 時間のかかった script/css の上位(ファイル名末尾と所要ms@通信方式) */
  slowResources: string[];
  /** 対象ファイルの通信方式ごとの件数(例: {"h3": 12, "h2": 3})。キャッシュから読んだものは "" になる */
  protocols: { [protocol: string]: number };
};

const MAX_SLOW_RESOURCES = 3;

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return "";
  }
};

/**
 * 起動が遅かったときの通信の内訳。Navigation/Resource Timing が使えなければ空。
 * host を渡すと、そのホストのファイルだけを対象にする(広告・計測タグは白画面と無関係なため)
 */
export const buildStartupNetworkTiming = (
  perf: Performance,
  host?: string
): StartupNetworkTiming => {
  const nav = perf.getEntriesByType?.("navigation")?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  const doc = nav
    ? {
        dns: Math.round(nav.domainLookupEnd),
        connect: Math.round(nav.connectEnd),
        ttfb: Math.round(nav.responseStart),
        response_end: Math.round(nav.responseEnd),
        dom_interactive: Math.round(nav.domInteractive),
        transfer: nav.transferSize,
        proto: nav.nextHopProtocol ?? "",
      }
    : null;
  const resources = (
    (perf.getEntriesByType?.("resource") ?? []) as PerformanceResourceTiming[]
  )
    .filter((r) => r.initiatorType === "script" || r.initiatorType === "link")
    .filter((r) => !host || hostOf(r.name) === host);
  const protocols: { [protocol: string]: number } = {};
  for (const r of resources) {
    const protocol = r.nextHopProtocol ?? "";
    protocols[protocol] = (protocols[protocol] ?? 0) + 1;
  }
  const slowResources = [...resources]
    .sort((a, b) => b.duration - a.duration)
    .slice(0, MAX_SLOW_RESOURCES)
    .map(
      (r) =>
        `${r.name.split("?")[0].slice(-40)}:${Math.round(r.startTime)}+${Math.round(r.duration)}@${r.nextHopProtocol ?? ""}`
    );
  return { doc, slowResources, protocols };
};

/** index.html を取得した通信方式。Navigation Timing が使えなければ null */
export const documentProtocol = (perf: Performance): string | null => {
  const nav = perf.getEntriesByType?.("navigation")?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  return nav?.nextHopProtocol || null;
};

/**
 * 起動が遅かったとして報告するか。
 * 起動直後に裏へ回り、しばらくして戻ってきた起動は、裏にいた時間で遅く見えるだけなので除く
 * (リリース後の記録で、表示時間 0.2〜0.3 秒・全体 35〜243 秒の誤検知があった)。
 * 表示されたまま長く待った後に裏へ回った起動(待ちきれずに離れた)は残す
 */
export const shouldReportSlow = (
  trace: StartupTrace,
  totalMs: number,
  thresholdMs: number,
  minVisibleMsIfHidden: number
): boolean => {
  // 裏で起動された(プリウォーム等)起動はユーザーが待っていない
  if (trace.marks[0]?.[0] !== "app_start:visible" || totalMs < thresholdMs) {
    return false;
  }
  const wentHidden = trace.marks.some(([name]) => name === "vis:hidden");
  return !wentHidden || trace.visibleMs >= minVisibleMsIfHidden;
};

export type StartupDomSnapshot = {
  route: string;
  readyState: string;
  visibility: string;
  online: boolean;
  /** アプリ直下の outlet に載っているページ。tag と表示状態のクラス */
  rootPages: string[];
  /** ion-tabs があれば選択中のタブ */
  tab: string | null;
  /** 表示中のオーバーレイ(ローディングが残って白く見えるケースの切り分け) */
  overlays: string[];
  bodyTextLength: number;
};

const PAGE_STATE_CLASSES = ["ion-page-invisible", "ion-page-hidden"];
const OVERLAY_SELECTOR = "ion-loading, ion-alert, ion-modal, ion-popover";

const describePage = (el: Element): string => {
  const states = PAGE_STATE_CLASSES.filter((c) => el.classList.contains(c));
  return states.length > 0
    ? `${el.tagName.toLowerCase()}[${states.join(",")}]`
    : el.tagName.toLowerCase();
};

/** 起動が止まったときの画面の状態。白画面が「ページ不可視のまま」か「ページ自体が無い」かを切り分ける */
export const buildStartupDomSnapshot = (doc: Document): StartupDomSnapshot => {
  const outlet = doc.querySelector("ion-app > ion-router-outlet");
  const rootPages = outlet
    ? Array.from(outlet.children).slice(0, 5).map(describePage)
    : ["(no outlet)"];
  const tabs = doc.querySelector("ion-tabs");
  const selectedTabButton = doc.querySelector("ion-tab-button.tab-selected");
  const overlays = Array.from(doc.querySelectorAll(OVERLAY_SELECTOR))
    .filter((el) => !el.classList.contains("overlay-hidden"))
    .slice(0, 5)
    .map((el) => el.tagName.toLowerCase());
  return {
    route: (doc.location?.hash ?? "").replace(/\?.*$/, ""),
    readyState: doc.readyState,
    visibility: doc.visibilityState,
    online: navigator.onLine,
    rootPages,
    tab: tabs ? (selectedTabButton?.getAttribute("tab") ?? "") : null,
    overlays,
    bodyTextLength: doc.body?.innerText?.trim().length ?? 0,
  };
};
